import * as React from 'react';
import './Admin.less';
import { Form, Input, Button, Select, Divider, message, Spin, Row, Col, Radio, Upload, Icon } from 'antd';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { UploadChangeParam, RcFile } from "antd/lib/upload/interface";
import { PageProps } from 'src/models/common/ComponentProps';
import bindAllOfThis from "src/utils/BindThisHelper";

import DataTable, { DataTableColumnProps } from 'src/components/common/DataTable';

import MatchmakerApiController from 'src/api/MatchmakerApiController';
import TableRequestDTO from 'src/models/datatable/TableRequestDTO';
import TableResponseDTO from 'src/models/datatable/TableResponseDTO';
import SyncMatchmakerDataLogDTO from 'src/models/matchmaker/SyncMatchmakerDataLogDTO';
import ExcelRowValidationResultDTO from 'src/models/validation/ExcelRowValidationResultDTO';
import GenericBaseResponseDTO from 'src/models/validation/GenericBaseResponseDTO';
import UsersUploadErrorModal from './UsersUploadErrorModal';


interface SyncMatchmakerDataProps extends PageProps<{}>, LocalizeContextProps {
}

interface SyncMatchmakerDataState {
    loading: boolean;
    uploading: boolean;
    tableRecords: SyncMatchmakerDataLogDTO[];

    // Modal variables
    uploadStatus: "none" | "uploading" | "success" | "failed";
    uploadErrorMessage: string;
    uploadFileName: string;
    uploadTableData: ExcelRowValidationResultDTO[];
    showUploadModal: boolean;
}

class SyncMatchmakerData extends React.Component<SyncMatchmakerDataProps, SyncMatchmakerDataState> {
    private dataTableRef: DataTable<SyncMatchmakerDataLogDTO>;

    constructor(props: SyncMatchmakerDataProps) {
        super(props);
        bindAllOfThis(this, SyncMatchmakerData.prototype);

        // You are allowed to set defaults here, based on your state object above
        this.state = {
            loading: true,
            uploading: false,
            tableRecords: [],
            uploadStatus: "none",
            uploadErrorMessage: "",
            uploadFileName: "",
            uploadTableData: [],
            showUploadModal: false
        };
    }

    private fetchData(requestState: TableRequestDTO, checkEcho: () => boolean, callback: (response: TableResponseDTO<SyncMatchmakerDataLogDTO>) => void): void {
        this.setState({ loading: true });

        MatchmakerApiController.getSyncMatchmakerDataLogTableData(requestState)
            .then(results => {
                if (!checkEcho()) {
                    return;
                }

                let tableRecords = results.data.results ? results.data.results : [];

                this.setState({
                    tableRecords: tableRecords,
                    loading: false
                });

                callback(results.data);
            });
    }

    private customTableColumns(): DataTableColumnProps<SyncMatchmakerDataLogDTO>[] {
        return [
            DataTable.AdvancedColumns.Text(<Translate id='admin.syncMatchmakerHistory.columnHeader1' />, 'districtName'),
            DataTable.AdvancedColumns.Number(<Translate id='admin.syncMatchmakerHistory.columnHeader2' />, 'totalRecordsSynced'),
            DataTable.AdvancedColumns.Text(<Translate id='admin.syncMatchmakerHistory.columnHeader3' />, 'userFullName'),
            DataTable.AdvancedColumns.DateTime(<Translate id='admin.syncMatchmakerHistory.columnHeader4' />, 'createdDt')
        ];
    }

    private handleModelClose() {
        this.setState({ showUploadModal: false });
    }

    private handleBeforeUpload(file: RcFile, fileList: RcFile[]) {
        this.setState({ uploading: true, uploadStatus: "uploading", uploadErrorMessage: "" });
        return true;
    }

    private handleUploadOnChange(info: UploadChangeParam) {
        if (info.file.status === 'uploading') {
            return;
        }

        // We are finished uploading in some fashion
        this.setState({ uploading: false });

        // Get error from response. If it's a really bad error (500), then response will actually have a message attached to it rather than a proper response
        let response = (info.file.response || {}) as GenericBaseResponseDTO<ExcelRowValidationResultDTO[]>;
        let error = info.file.response != null && info.file.response.message != null ? info.file.response.message : response.errorMessage;

        if (info.file.status === 'done' && response.isSuccess) {
            message.success(info.file.name + " - " + this.props.translate('admin.users.messageUpload'));
            this.setState({
                uploadStatus: "success",
                uploadFileName: info.file.name,
                uploadErrorMessage: error,
                uploadTableData: response.data
            });
            this.dataTableRef.refresh();
        } else {
            message.error(info.file.name + " - " + this.props.translate('admin.users.messageUploadError'));
            this.setState({
                uploadStatus: "failed",
                uploadFileName: info.file.name,
                uploadErrorMessage: error,
                uploadTableData: response.data
            });
        }
    }

    private renderUploadCompleteIcon(): JSX.Element {
        if (this.state.uploadStatus === "success") {
            return (<Icon type="check-circle" theme="twoTone" twoToneColor="#52c41a" />);
        }

        if (this.state.uploadStatus === "failed") {
            return <Button onClick={() => this.setState({ showUploadModal: true })}><Icon type="exclamation-circle" theme="twoTone" twoToneColor="#c41a1a" /> View Upload Errors</Button>;
        }
    }

    private renderFileUpload(): JSX.Element {
        const uploadProps = {
            name: 'file',
            showUploadList: false,
            action: "/" + MatchmakerApiController.RouteMatchmakerDataUpload(),
            disabled: this.state.uploadStatus === "uploading" || this.state.uploading,
            beforeUpload: this.handleBeforeUpload,
            onChange: this.handleUploadOnChange,
            data: { isCompleteMode: true } // Dev Note: This is not used but the API needs it because reasons
        };
        return (
            <div className="mass-upload-buttons">
                <div>
                    {/* <span>
                        <Translate id="admin.users.massStudentUploadStep" /> 4:
                    </span> */}
                    <p>Select a file to upload</p>
                    <Upload {...uploadProps}>
                        <Button disabled={this.state.uploadStatus === "uploading" || this.state.uploading }>
                            <Icon type="upload" /> Upload Softmatch File
                        </Button>
                    </Upload>
                    <Spin spinning={this.state.uploading} />
                    {this.renderUploadCompleteIcon()}
                </div>
            </div>
        );
    }

    render() {
        return (
            <div className="admin-sync-matchmaker-data">
                <UsersUploadErrorModal
                    errorMessage={this.state.uploadErrorMessage}
                    fileName={this.state.uploadFileName}
                    tableData={this.state.uploadTableData}
                    visible={this.state.showUploadModal}
                    onOkay={this.handleModelClose}
                />
                <h2>Sync Matchmaker Data</h2>
                {this.renderFileUpload()}
                <Divider type="horizontal" />
                <h2>Matchmaker Sync History</h2>
                <DataTable
                    ref={ref => this.dataTableRef = ref}
                    serverSide={true}
                    // tableProps={{ rowKey: 'userId', loading: this.state.loading }}
                    tableProps={{ loading: this.state.loading }}
                    columns={this.customTableColumns()}
                    data={this.state.tableRecords}
                    fetchData={this.fetchData}
                />
            </div>
        );
    }
}

export default withLocalize(SyncMatchmakerData);



// WEBPACK FOOTER //
// ./src/pages/admin/SyncMatchmakerData.tsx